import * as React from "react";
import { ConnectProto } from "@racoon/api";
import { useLang } from "@racoon/i18n";
import { useListTeamsQuery } from "~/shared/api/team/team.grpc";

/** Key of the group of drivers in no team. */
export const NO_TEAM = "no-team";

/** The organization's teams of one role, by name. */
export const useRoleTeams = (
  organizationId: string | number | bigint | null | undefined,
  role: ConnectProto.UserRole | undefined,
  skip = false,
) => {
  const { lang } = useLang();
  // `currentData`: after an org or role switch, never the previous one's teams.
  const { currentData } = useListTeamsQuery(
    {
      ownerOrganizationIds: organizationId ? [BigInt(organizationId)] : [],
      userRoles: role !== undefined ? [role] : [],
    },
    { skip: !organizationId || role === undefined || skip },
  );
  return React.useMemo(
    () => [...(currentData?.teams ?? [])].sort((a, b) => a.name.localeCompare(b.name, lang)),
    [currentData, lang],
  );
};

/** The organization's driver teams, by name. */
export const useDriverTeams = (organizationId?: string | number | null, skip = false) =>
  useRoleTeams(organizationId, ConnectProto.UserRole.DRIVER, skip);

export type TeamGroup<T> = { key: string; label?: string; team?: ConnectProto.Team; items: T[] };

/**
 * `items` grouped by team, in the teams' order (`team-<id>` keys). Teams and
 * users are many-to-many, so a driver in several teams is listed under each;
 * drivers in none go to a last NO_TEAM group, and empty groups are dropped.
 * With no teams at all there is one group, without a label. Items keep their
 * input order within a group.
 */
export const groupByTeam = <T>(
  items: T[],
  teams: ConnectProto.Team[],
  driverIdOf: (item: T) => string,
  noTeamLabel: string,
): TeamGroup<T>[] => {
  if (teams.length === 0) return items.length > 0 ? [{ key: "", items }] : [];
  const teamed = new Set<string>();
  const groups: TeamGroup<T>[] = teams.map((team) => {
    const ids = new Set(team.users.map((user) => String(user.id)));
    ids.forEach((id) => teamed.add(id));
    return {
      key: `team-${team.id}`,
      label: team.name,
      team,
      items: items.filter((item) => ids.has(driverIdOf(item))),
    };
  });
  groups.push({
    key: NO_TEAM,
    label: noTeamLabel,
    items: items.filter((item) => !teamed.has(driverIdOf(item))),
  });
  return groups.filter((group) => group.items.length > 0);
};
